/**
 * Shipping Rates Resource Client
 * Axios-based API client for shipping rates endpoints
 */

import { ApiClient } from './apiClient';

export interface ShippingRate {
    id: string;
    shippingRateName: string;
    amount: number;
    currency: string;
    isFree: boolean;
    description?: string;
    highlighted?: boolean;
    icon?: string;
    isPickupPoint?: boolean;
    pickupPointCarriers?: string[];
}

export interface ShippingRatesResponse {
    rates: ShippingRate[];
    forceCheckoutSessionRefetch?: boolean;
}

export interface ShippingRatesPreviewParams {
    countryCode: string;
    stateCode?: string;
}

export interface ShippingRatesPreviewResponse {
    rates: ShippingRate[];
    pickupPoint?: {
        apiKey: string | null;
    };
}

export class ShippingRatesResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Get shipping rates for a checkout session
     */
    async getShippingRates(sessionId: string): Promise<ShippingRatesResponse> {
        return this.apiClient.get(`/api/v1/checkout-sessions/${sessionId}/shipping-rates`);
    }

    /**
     * Set selected shipping rate for a checkout session
     */
    async setShippingRate(
        sessionId: string,
        shippingRateId: string,
    ): Promise<{ success: boolean; error?: any }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${sessionId}/shipping-rate`, {
            shippingRateId,
        });
    }

    /**
     * Preview shipping rates for a country without updating session
     * Useful for showing rates before user enters email
     */
    async previewShippingRates(
        sessionId: string,
        params: ShippingRatesPreviewParams,
    ): Promise<ShippingRatesPreviewResponse> {
        const queryParams = new URLSearchParams({ countryCode: params.countryCode });
        if (params.stateCode) {
            queryParams.set('stateCode', params.stateCode);
        }
        return this.apiClient.get(
            `/api/v1/checkout-sessions/${sessionId}/shipping-rates/preview?${queryParams.toString()}`,
        );
    }
}

